import type {
  InsuranceCardSide,
  IPatientInsuranceCard,
  IPatientInsuranceCardImage,
} from "../../../core/ports/driver/VisitNotesService";

export const buildPatientInsuranceCardUrl = ({
  route,
  clinicId,
  patientId,
  side,
}: {
  route: string;
  clinicId: number;
  patientId: number;
  side?: InsuranceCardSide;
}): string => {
  const url = route
    .replace("{clinic_id}", clinicId.toString())
    .replace("{patient_id}", patientId.toString());
  return side ? url.replace("{side}", side) : url;
};

const insuranceCardImageFromPayload = (
  value: unknown,
  side: InsuranceCardSide
): IPatientInsuranceCardImage | null => {
  if (typeof value === "string" && value.trim()) {
    return { side, url: value.trim() };
  }
  if (!value || typeof value !== "object") return null;

  const image = value as Record<string, unknown>;
  const url = [
    image.url,
    image.presigned_url,
    image.image_url,
    image.download_url,
  ].find((candidate) => typeof candidate === "string" && candidate.trim()) as
    | string
    | undefined;
  if (!url) return null;

  return {
    ...image,
    side,
    url: url.trim(),
    filename: typeof image.filename === "string" ? image.filename : undefined,
    content_type:
      typeof image.content_type === "string" ? image.content_type : undefined,
    uploaded_at:
      typeof image.uploaded_at === "string"
        ? image.uploaded_at
        : typeof image.created_at === "string"
        ? image.created_at
        : undefined,
  };
};

export const insuranceCardFromApiResponse = (
  response: unknown
): IPatientInsuranceCard => {
  const responseRecord =
    response && typeof response === "object"
      ? (response as Record<string, unknown>)
      : {};
  const unwrapped =
    responseRecord.data && typeof responseRecord.data === "object"
      ? (responseRecord.data as Record<string, unknown>)
      : responseRecord;
  const cardValue =
    unwrapped.insurance_card !== undefined
      ? unwrapped.insurance_card
      : unwrapped.images !== undefined
      ? unwrapped.images
      : unwrapped;
  const card =
    cardValue && typeof cardValue === "object"
      ? (cardValue as Record<string, unknown>)
      : {};

  return {
    front: insuranceCardImageFromPayload(
      card.front ?? card.front_url ?? unwrapped.front_url,
      "front"
    ),
    back: insuranceCardImageFromPayload(
      card.back ?? card.back_url ?? unwrapped.back_url,
      "back"
    ),
  };
};
